/**
 * This is the Billing-Pricing Tables middleware.
 * APIs are called for CRUD operations on the list.
 * @param store
 * @returns {function(*): function(...[*]=)}
 */

import API from "../../common/utils/API";
import {toUpperCaseSentence} from "../../common/utils/ToUpperCaseSentence";
import {
    displayDuplicateMessage,
    displayErrorMessage,
    displaySuccessMessage,
    displayWarningMessage
} from "../../common/marvel-reducers/formMessageActions";
import MARVEL from "../../common/utils/MarvelUrls";
import { setRouterLink } from "../../router/RouterActions";

import {
    addCountryLevelPricingV2,
    addPricingTableV2,
    closeCountryPricingModalV2,
    editCountryLevelPricingV2,
    editPricingTableV2,
    fetchCountryPricesV2,
    fetchOnePricingTableV2,
    countryPricingRecordsSuccessV2,
    pricingTableDetailSuccessV2,
    pricingTablesTableDataSuccessV2,
    toggleAddPricingTableModalV2,
    toggleDeleteCountryPriceModalV2,
    toggleDeletePricingTableModalV2,
    toggleEditPricingTableModalV2,
    fetchPricingTablesTableDataV2
} from "../../billing/pricing-tables/actions/PricingTablesActions";
import {
    addEditPricingTableActionTypes,
    apiActionsCountryPricing,
    apiActionsPricingTables, CountryPricingActionTypes
} from "../../billing/pricing-tables/constants/PricingTablesActionConstants";
import * as Validation from "../../common/utils/Validation";


const pricingTablesMiddleware = (store) => (next) => (action) => {

    switch (action.type) {

        case apiActionsPricingTables.GET_V2: {
            next(action);

            const { authToken, pricingID } = action.payload;
            API.getOnePricingTableV2(authToken.idToken, pricingID)
                .then(res => {
                    store.dispatch(pricingTableDetailSuccessV2(res.data))
                }).catch(err => {
                console.error(err);
            });
            break;
        }

        case apiActionsPricingTables.GET_ALL_V2: {
            next(action);

            const { authToken } = action.payload;
            API.getAllPricingTablesV2(authToken.idToken)
                .then(res => {
                    store.dispatch(pricingTablesTableDataSuccessV2(res.data))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case apiActionsPricingTables.POST_V2: {
            next(action);

            const { authToken, pricingTableObjectV2 } = action.payload;
            if (authToken.account.name) {
                pricingTableObjectV2.updated_by = authToken.account.name;
            }
            API.postPricingTableV2(authToken.idToken, pricingTableObjectV2)
                .then(res => {
                    store.dispatch(toggleAddPricingTableModalV2());
                    // go directly to newly created pricing table
                    store.dispatch(fetchOnePricingTableV2(res.data.pricing_id));
                    store.dispatch(fetchCountryPricesV2(res.data.pricing_id))
                    store.dispatch(setRouterLink(MARVEL.BILLING.pricing_tables_v2 + res.data.pricing_id))
                }).catch(error => {
                console.error(error);
            });
            break;
        }

        case apiActionsPricingTables.PUT_V2: {
            next(action);

            const { authToken, pricingTableObjectV2, pricingID } = action.payload;
            if (authToken.account.name) {
                pricingTableObjectV2.updated_by = authToken.account.name;
            }
            API.putPricingTableV2(authToken.idToken, pricingID, pricingTableObjectV2)
                .then(res => {
                    store.dispatch(toggleEditPricingTableModalV2());
                    store.dispatch(fetchOnePricingTableV2(pricingID));
                    store.dispatch(fetchCountryPricesV2(pricingID));
                })
                .catch(e => {
                    const errorObject = e.response.data;
                    const errorMessage = [];
                    // pretty print the error message for the user
                    // eslint-disable-next-line no-unused-vars
                    for (let [key, value] of Object.entries(errorObject)) {
                        errorMessage.push(toUpperCaseSentence(value));
                    }
                    store.dispatch(displayErrorMessage(errorMessage, true));
                });

            break;
        }

        case apiActionsPricingTables.DELETE_V2: {
            next(action);

            const { authToken, pricingID } = action.payload;
            API.deletePricingTableV2(authToken.idToken, pricingID)
                .then(res => {
                    store.dispatch(toggleDeletePricingTableModalV2());
                    store.dispatch(setRouterLink(MARVEL.BILLING.pricing_tables_v2))

                }).catch(error => {
                const errorObject = error.response.data
                const message = [];
                // pretty print the error message for the user
                // eslint-disable-next-line no-unused-vars
                for (let [key, value] of Object.entries(errorObject)) {
                    message.push(toUpperCaseSentence(value[0]));
                }
                store.dispatch(displayErrorMessage(message, true));
            });

            break;
        }

        case addEditPricingTableActionTypes.VALIDATE_PRICING_TABLE_FORM_V2: {
            next(action);

            const { pricingTableObjectV2 } = action.payload;
            // explicitly typecasting into number.
            pricingTableObjectV2.base_price = Number(pricingTableObjectV2.base_price)

            // Call the custom validation for the fields.
            // else/invalid inputs are handled by Validation.js which sends out a
            // form error/warning message through redux.
            if (Validation.validatePricingTableFormV2(pricingTableObjectV2)) {
                // check if the redux action dispatched is for editing the pricing table
                if (pricingTableObjectV2.pricing_id) {
                    const pricingID = pricingTableObjectV2.pricing_id;
                    store.dispatch(editPricingTableV2(pricingTableObjectV2, pricingID))
                } else {
                    store.dispatch(addPricingTableV2(pricingTableObjectV2))
                }
            }

            break;
        }

        case CountryPricingActionTypes.VALIDATE_EDIT_COUNTRY_PRICING_FORM_V2: {
            next(action);

            const {countryPricingObjectV2} = action.payload;
            const countryPricingID = countryPricingObjectV2.country_pricing_id;
            if (Validation.validateCountryPricingFormV2(countryPricingObjectV2)) {
                if (countryPricingID) {
                    store.dispatch(editCountryLevelPricingV2(countryPricingID, countryPricingObjectV2));
                } else {
                    // make the payload compatible with what the API expects
                    countryPricingObjectV2.pricing = countryPricingObjectV2.pricing_id;
                    delete countryPricingObjectV2.pricing_id;
                    store.dispatch(addCountryLevelPricingV2(countryPricingObjectV2));
                }
            }

            break;
        }

        case apiActionsCountryPricing.GET_V2: {
            next(action);

            const { authToken, pricingID } = action.payload;
            API.getCountryPricingRecordsV2(authToken.idToken, pricingID)
                .then(res => {
                    store.dispatch(countryPricingRecordsSuccessV2(res.data))
                }).catch(err => {
                console.error(err);
            });
            break;
        }

        case apiActionsCountryPricing.PUT_V2: {
            next(action);

            const {authToken, countryPricingID, countryPricingObjectV2} = action.payload;
            if (authToken.account.name) {
                countryPricingObjectV2.updated_by = authToken.account.name;
            }
            API.putCountryPricingV2(authToken.idToken, countryPricingID, countryPricingObjectV2)
                .then(res => {
                    store.dispatch(closeCountryPricingModalV2());
                    store.dispatch(fetchOnePricingTableV2(res.data.pricing));
                    store.dispatch(fetchCountryPricesV2(res.data.pricing))
                })
                .catch(error => {
                    if (error.response.status === 409) {
                        const message = ["This country already exists in the pricing table."]
                        store.dispatch(displayDuplicateMessage(message, true));
                    }
                    console.error(error);
                })

            break;
        }

        case apiActionsCountryPricing.POST_V2: {
            next(action);

            const {authToken, countryPricingObjectV2} = action.payload;
            if (authToken.account.name) {
                countryPricingObjectV2.updated_by = authToken.account.name;
            }
            API.postCountryPricingV2(authToken.idToken, countryPricingObjectV2)
                .then(res => {
                    store.dispatch(closeCountryPricingModalV2());
                    store.dispatch(fetchOnePricingTableV2(res.data.pricing));
                    store.dispatch(fetchCountryPricesV2(res.data.pricing))
                })
                .catch(error => {
                    if (error.response.status === 409) {
                        const message = ["This country already exists in the pricing table."]
                        store.dispatch(displayErrorMessage(message, true));
                    }
                    console.error(error);
                })

            break;
        }

        case apiActionsCountryPricing.DELETE_V2: {
            next(action);

            const {authToken, pricingID, countryPricingID} = action.payload;
            API.deleteCountryPricingV2(authToken.idToken, countryPricingID)
                .then(res => {
                    store.dispatch(toggleDeleteCountryPriceModalV2());
                    store.dispatch(fetchOnePricingTableV2(pricingID));
                    store.dispatch(fetchCountryPricesV2(pricingID))
                })
                .catch(error => {
                    console.error(error);
                })

            break;
        }

        case apiActionsPricingTables.UPLOAD_PRICING_DATA: {
            next(action);

            const { authToken, file } = action.payload;
            API.postUploadPricingData(authToken.idToken, file)
                .then(res => {
                    const data = res.data;
                    let successMessage = [];

                    if (data.success && data.worksheets_processed) {
                        // Add summary message
                        successMessage.push(`Total worksheets processed: ${data.worksheets_processed}`);

                        // Add details for each worksheet if available
                        if (data.results && Array.isArray(data.results)) {
                            data.results.forEach(result => {
                                if (result.success) {
                                    successMessage.push(`${result.worksheet}: Created pricing table "${result.pricing_table_name}" with ${result.country_records_created} country records`);
                                }
                            });
                        }

                        // Add errors for failed worksheets if available
                        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
                            const warningHeader = "PARTIAL SUCCESS: ";
                            successMessage.push("However, some worksheets failed to process:");
                            data.errors.forEach(err => {
                                successMessage.push(`${err}`);
                            });
                            store.dispatch(displayWarningMessage(successMessage, true, warningHeader));
                        } else {
                            // Complete success - no errors
                            store.dispatch(displaySuccessMessage(successMessage, true));
                        }
                    } else {
                        // Fallback message
                        successMessage = ["Pricing data uploaded and processed successfully!"];
                        store.dispatch(displaySuccessMessage(successMessage, true));
                    }
                    // Refresh the pricing tables data after successful upload
                    store.dispatch(fetchPricingTablesTableDataV2());
                })
                .catch(error => {
                    let errorMessage = ["Failed to upload pricing data. Please try again."];

                    // Handle the specific error format from the endpoint
                    if (error.response?.data?.error) {
                        if (error.response.data.details && Array.isArray(error.response.data.details)) {
                            errorMessage = error.response.data.details;
                        }
                    }

                    store.dispatch(displayErrorMessage(errorMessage, true));
                    console.error(error);
                });
            break;
        }

        default: next(action);
    }
}

export default pricingTablesMiddleware;
